import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Suspense, lazy } from 'react';
import { AppProvider } from '@/contexts/AppProvider';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { shouldShowDesignSystemPage, shouldShowSentryDebugPage } from '@/lib/featureFlags';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Lazy-loaded Pages for better performance
const Dashboard = lazy(() =>
  import('./pages/dashboard/Dashboard').then(m => ({ default: m.Dashboard }))
);
const Assets = lazy(() =>
  import('./pages/dashboard/assets/Assets').then(m => ({ default: m.Assets }))
);
const Tags = lazy(() => import('./pages/dashboard/tags/Tags').then(m => ({ default: m.Tags })));
const Cycles = lazy(() =>
  import('./pages/dashboard/cycles/Cycles').then(m => ({ default: m.Cycles }))
);
const Tracking = lazy(() =>
  import('./pages/dashboard/tracking/Tracking').then(m => ({ default: m.Tracking }))
);
const Factory = lazy(() =>
  import('./pages/dashboard/factory/Factory').then(m => ({ default: m.Factory }))
);
const Reports = lazy(() =>
  import('./pages/dashboard/reports/Reports').then(m => ({ default: m.Reports }))
);
const AlertsPage = lazy(() =>
  import('./pages/dashboard/alerts/Alerts').then(m => ({ default: m.Alerts }))
);
const LocationAnalytics = lazy(() =>
  import('./pages/dashboard/location-analytics/LocationAnalytics').then(m => ({
    default: m.LocationAnalytics,
  }))
);
const SettingsHub = lazy(() =>
  import('./pages/dashboard/settings/SettingsHub').then(m => ({ default: m.SettingsHub }))
);
const ProfilePage = lazy(() =>
  import('./pages/dashboard/settings/profile/ProfilePage').then(m => ({ default: m.ProfilePage }))
);
const RolesPage = lazy(() =>
  import('./pages/dashboard/settings/roles/RolesPage').then(m => ({ default: m.RolesPage }))
);
const Users = lazy(() => import('./pages/users-page').then(m => ({ default: m.UsersPage })));
const ComingSoonSection = lazy(() =>
  import('./pages/dashboard/coming-soon/ComingSoonSection').then(m => ({
    default: m.ComingSoonSection,
  }))
);

const Login = lazy(() => import('./pages/auth/Login').then(m => ({ default: m.Login })));
const AcceptInvitation = lazy(() =>
  import('./pages/auth/AcceptInvitation').then(m => ({ default: m.AcceptInvitation }))
);
const ComingSoonPage = lazy(() =>
  import('./pages/legal/ComingSoonPage').then(m => ({ default: m.ComingSoonPage }))
);
const DesignSystem = lazy(() =>
  import('./pages/dashboard/design-system/DesignSystemPage').then(m => ({
    default: m.DesignSystemPage,
  }))
);
const DebugSentry = lazy(() =>
  import('./pages/dashboard/debug-sentry/DebugSentryPage').then(m => ({
    default: m.DebugSentryPage,
  }))
);

function App(): React.JSX.Element {
  return (
    <AppProvider>
      <div className="min-h-screen bg-background">
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          }
        >
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/invite/accept" element={<AcceptInvitation />} />
            <Route
              path="/privacy-policy"
              element={<ComingSoonPage titleKey="legal.privacy_policy_title" />}
            />
            <Route
              path="/terms-of-service"
              element={<ComingSoonPage titleKey="legal.terms_of_service_title" />}
            />
            {shouldShowSentryDebugPage() && (
              <Route path="/debug/sentry" element={<DebugSentry />} />
            )}

            {/* Protected Dashboard Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route
                path="tracking"
                element={
                  <ProtectedRoute requiredPermission="view:tracking">
                    <Tracking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <ProtectedRoute requiredPermission="view:reports">
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="alerts"
                element={
                  <ProtectedRoute requiredPermission="view:reports">
                    <AlertsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="location-analytics"
                element={
                  <ProtectedRoute requiredPermission="view:location-analytics">
                    <LocationAnalytics />
                  </ProtectedRoute>
                }
              />

              {/* Settings hub */}
              <Route path="settings" element={<SettingsHub />} />
              <Route path="settings/profile" element={<ProfilePage />} />
              <Route
                path="settings/roles"
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <RolesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/assets"
                element={
                  <ProtectedRoute requiredPermission="view:assets">
                    <Assets />
                  </ProtectedRoute>
                }
              />
              <Route path="settings/tags" element={<Tags />} />
              <Route path="settings/factory" element={<Factory />} />
              <Route
                path="settings/users"
                element={
                  <ProtectedRoute requiredPermission="view:users">
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/cycles"
                element={
                  <ProtectedRoute requiredPermissions={['view:cycles', 'view:reports']}>
                    <Cycles />
                  </ProtectedRoute>
                }
              />

              {/* Permanent redirects — app is live in production, these paths are bookmarked */}
              <Route path="settings/user" element={<Navigate to="/settings/profile" replace />} />
              <Route path="assets" element={<Navigate to="/settings/assets" replace />} />
              <Route path="tags" element={<Navigate to="/settings/tags" replace />} />
              <Route path="factory" element={<Navigate to="/settings/factory" replace />} />
              <Route path="users" element={<Navigate to="/settings/users" replace />} />
              <Route path="cycles" element={<Navigate to="/settings/cycles" replace />} />

              {/* In-app footer link placeholders — see DEV2-1134 */}
              <Route path="docs" element={<ComingSoonSection titleKey="layout.footer.docs" />} />
              <Route
                path="support"
                element={<ComingSoonSection titleKey="layout.footer.support" />}
              />
              <Route
                path="system-status"
                element={<ComingSoonSection titleKey="layout.footer.systemStatus" />}
              />
              <Route
                path="feedback"
                element={<ComingSoonSection titleKey="layout.footer.feedback" />}
              />

              {shouldShowDesignSystemPage() && (
                <Route path="design-system" element={<DesignSystem />} />
              )}
            </Route>
          </Routes>
        </Suspense>
      </div>
      <Toaster
        position="top-right"
        richColors
        closeButton
        duration={4000}
        toastOptions={{ className: 'font-sans' }}
      />
    </AppProvider>
  );
}

export { App };
